/* eslint-disable no-undef */
import {
  ThreatEvaluationRequestEntity,
  ThreatEvaluationRequestProduct,
  ThreatEvaluationResponseEntity,
  ThreatEvaluationResponseProduct,
} from 'interfaces/AIEvalInterfaces';
import axios from 'axios';
import KeycloakService from 'services/KeycloakService';

// -------- Backend type stubs --------
type OpenSearchDoc = {
  id?: string;
  name: string;
  list_name: string;
  country?: string;
  address?: string;
  aka?: string[];
  source?: string;
  updated_at?: string;
  related_entity?: string;
  bite_lens?: string | string[];
};

type BestMatch = {
  id?: string;
  name: string;
  confidence: number;
  keep_reason?: string;
};

type CountryRisk = {
  iso?: string;
  tier?: 'Low' | 'Medium' | 'High';
  category?: string;
  is_sanctioned?: boolean;
  is_embargo?: boolean;
  is_hub?: boolean;
  unknown?: boolean;
  flag?: string;
};

type RiskAssessment = {
  overall_risk: 'Low' | 'Medium' | 'High' | 'Unknown';
  rationale: string;
  per_match: Array<{
    doc_id?: string;
    risk: string;
    rules_triggered: string[];
  }>;
  error?: string;
  best_matches?: BestMatch[];
  country_risk?: CountryRisk | null;
  rating_driver?: string | null;
  tier_table_version?: string | null;
};

type BestMatchRequest = {
  entity: { name: string; country?: string; address?: string };
  results: OpenSearchDoc[];
};

// -------- helpers --------
const pick = (obj: any, ...paths: string[]) => {
  for (const p of paths) {
    const v = p.split('.').reduce((o, k) => (o ? o[k] : undefined), obj);
    if (v !== undefined && v !== null && `${v}`.trim() !== '') {
      return v;
    }
  }
  return undefined;
};

const toArray = (v: any): string[] | undefined => {
  if (v === null) {
    return undefined;
  }
  if (Array.isArray(v)) {
    return v.map((x) => `${x}`.trim()).filter(Boolean);
  }
  if (typeof v === 'string') {
    return v
      .split(/[;,|]/g)
      .map((s) => s.trim())
      .filter(Boolean);
  }
  return undefined;
};

const toBestMatchRequest = (
  req: ThreatEvaluationRequestEntity,
): BestMatchRequest => {
  let parsed: any = null;
  try {
    parsed = JSON.parse(req.possible_matches_json);
  } catch {
    parsed = [];
  }

  // Accept either raw array or OpenSearch format
  const arr: any[] = Array.isArray(parsed)
    ? parsed
    : Array.isArray(parsed?.hits?.hits)
    ? parsed.hits.hits
    : [];

  const results: OpenSearchDoc[] = arr
    .map((d: any) => {
      const src = d._source ?? d.source ?? d;

      const name =
        pick(src, 'Name of Entity', 'name', 'entity_name', 'title') ?? '';
      const listName =
        pick(src, 'List Name', 'list_name', 'listName', 'index') ??
        pick(d, '_index') ??
        '';

      const akaRaw = pick(src, 'Alternative Names', 'aka', 'aliases');

      return {
        id:
          pick(src, 'BITE Record ID', 'Record ID', 'record_id') ??
          pick(d, '_id', 'id', 'meta.id', '_source.id'),
        name: name,
        list_name: listName,
        country: pick(src, 'Entity Country', 'country', 'country_iso'),
        address: pick(src, 'Entity Address', 'address'),
        aka: toArray(akaRaw),
        source: pick(src, 'Agency', 'source') ?? pick(d, '_index'),
        updated_at: pick(
          src,
          'Date Updated on BITE Platform',
          'updated_at',
          'updatedAt',
        ),
        related_entity: pick(src, 'Related Entity', 'related_entity'),
        bite_lens: pick(src, 'BITE Lens', 'Lens'),
        ...src,
      } as OpenSearchDoc;
    })
    .filter((r) => (r.name || '').trim().length > 0);

  return {
    entity: {
      name: req.entity_name,
      country: req.country || undefined,
      address: req.address || undefined,
    },
    results: results,
  };
};

const normalizeNameExact = (s: string) =>
  s
    .toLowerCase()
    .normalize('NFD')
    .replace(/\p{M}+/gu, '')
    .replace(/[.,'’\-_/]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();

const dedupeExactCanonical = <T extends { name?: string }>(arr: T[]) => {
  const seen = new Set<string>();
  const out: T[] = [];
  for (const m of arr) {
    const key = normalizeNameExact(m.name || '');
    if (!key) {
      continue;
    }
    if (seen.has(key)) {
      continue;
    }
    seen.add(key);
    out.push(m);
  }
  return out;
};

type OSINTCombinedBlock = {
  findings: Array<{
    claim: string;
    source: string;
    url: string;
    date?: string;
    confidence?: number;
  }>;
  confidence: number;
  sources: string[];
  summary?: string;
};

type OSINTCombinedResponse = { combined: OSINTCombinedBlock };

// -------- Risk-config (powers the "How do we evaluate risk?" modal) --------
export type RiskConfigCountry = { code: string; name: string };
export type RiskConfigGradedTier = {
  code: string;
  name: string;
  tier?: string;
  category?: string;
};
export type RiskConfig = {
  tier_table_version?: string;
  effective_date?: string;
  min_confidence_for_evaluation?: number;
  embargo_countries: RiskConfigCountry[];
  high_risk_countries: RiskConfigCountry[];
  diversion_hubs: RiskConfigCountry[];
  graded_tiers: RiskConfigGradedTier[];
};

export const fetchRiskConfig = async (): Promise<RiskConfig | null> => {
  const URL = `${process.env.REACT_APP_PYAPI_URL}/screening/risk-config`;
  try {
    const { data } = await axios.get<RiskConfig>(URL, {
      headers: {
        Accept: 'application/json',
        Authorization: `Bearer ${KeycloakService.getToken()}`,
      },
      timeout: 20000,
    });
    return data;
  } catch (error: any) {
    console.error(`[AI] Error fetching risk config: ${error?.message || error}`);
    return null;
  }
};

export const runEntityOSINTCombined = async (
  name: string,
  country: string,
  address: string,
  aliases: string[] = [],
): Promise<OSINTCombinedBlock | null> => {
  const URL = `${process.env.REACT_APP_PYAPI_URL}/screening/osint`;
  const payload = {
    entity: {
      name: name,
      country: country || undefined,
      address: address || undefined,
    },
    aliases: aliases,
  };

  try {
    const { data } = await axios.post<OSINTCombinedResponse>(URL, payload, {
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
        Authorization: `Bearer ${KeycloakService.getToken()}`,
      },
      timeout: 60000,
    });
    return data.combined;
  } catch (error: any) {
    console.error(
      `[AI] Error fetching OSINT combined: ${error?.message || error}`,
    );
    return null;
  }
};

// -------- formatter back to UI shape --------
const toThreatEvaluationResponseEntity = (
  entityName: string,
  bestMatches: BestMatch[],
  risk: RiskAssessment,
): ThreatEvaluationResponseEntity => {
  const sorted = [...(bestMatches || [])].sort(
    (a, b) => (b.confidence ?? 0) - (a.confidence ?? 0),
  );
  const deduped = dedupeExactCanonical(sorted);
  const top = deduped.slice(0, 5).map((m) => ({
    id: m.id,
    name: m.name,
    confidence: m.confidence,
  }));

  return {
    entity_name: entityName,
    best_match: top[0]?.name ?? '',
    risk: risk?.overall_risk ?? '',
    match_probability:
      typeof top[0]?.confidence === 'number' ? top[0].confidence : 0,
    explanation: risk?.rationale ?? 'No rationale provided.',
    screenDate: new Date().toLocaleDateString(),
    top_matches: top,
    // Country-risk signal (CR-002/004/005) for the inline notes, plus the
    // tier-table version (CR-006) — carried through so it persists on a saved
    // ticket and can explain a past rating.
    country_risk: risk?.country_risk ?? undefined,
    rating_driver: risk?.rating_driver ?? undefined,
    tier_table_version: risk?.tier_table_version ?? undefined,
    // osint intentionally omitted here (moved to its own call/UI box)
  } as any as ThreatEvaluationResponseEntity;
};

// -------- New: risk-only evaluate (single call) --------
export const runRiskEvaluate = async (
  request: ThreatEvaluationRequestEntity,
): Promise<ThreatEvaluationResponseEntity | null> => {
  const riskURL = `${process.env.REACT_APP_PYAPI_URL}/screening/risk`;

  const payload = toBestMatchRequest(request);

  try {
    // Single round trip: /screening/risk computes the best matches itself and
    // returns them alongside the risk assessment (no separate best-matches call).
    const riskRes = await axios.post<RiskAssessment>(riskURL, payload, {
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
        Authorization: `Bearer ${KeycloakService.getToken()}`,
      },
      timeout: 60000,
    });

    const bestMatches = riskRes.data?.best_matches || [];

    return toThreatEvaluationResponseEntity(
      payload.entity.name,
      bestMatches,
      riskRes.data,
    );
  } catch (error: any) {
    console.error(
      `[AI] Error in risk evaluation flow: ${error?.message || error}`,
    );
    // A processing error must not read as a clean/low-risk result. Surface an
    // explicit unscored state so the user knows to retry.
    return {
      entity_name: request.entity_name,
      best_match: '',
      risk: 'Unknown',
      match_probability: 0,
      explanation:
        'This entity could not be evaluated due to a processing error, so no risk level was assigned. Please try running the screening again.',
      screenDate: new Date().toLocaleDateString(),
    };
  }
};

// Keep this helper, now wired to the new two-step flow
export const runAIEvaluationSingleEntity = async (
  name: string,
  country: string,
  address: string,
  hits: string,
): Promise<ThreatEvaluationResponseEntity | null> => {
  const request: ThreatEvaluationRequestEntity = {
    entity_name: name,
    country: country,
    address: address,
    possible_matches_json: hits,
  };
  return runRiskEvaluate(request);
};

// --------- (unchanged) product functions ---------
const runAIEvaluationProducts = async (
  request: ThreatEvaluationRequestProduct,
  type: string,
): Promise<ThreatEvaluationResponseProduct | null> => {
  const URL =
    type === 'Import'
      ? `${process.env.REACT_APP_PYAPI_URL}/evaluate_import_threat`
      : `${process.env.REACT_APP_PYAPI_URL}/evaluate_export_threat`;
  return axios
    .post(URL, request, {
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
        Authorization: `Bearer ${KeycloakService.getToken()}`,
      },
      timeout: 60000,
    })
    .then((res) => {
      const { data, headers } = res as {
        data: ThreatEvaluationResponseProduct;
        headers?: Record<string, string>;
      };
      // The Import Workflow's screening step ran as the team's free trial run: the server says so,
      // and the caller charges nothing for it.
      return headers?.['x-bite-trial-run'] === 'true' ? { ...data, trial_run: true } : data;
    })
    .catch((error) => {
      console.error(`[AI] Error evaluating threat level: ${error.message}`);
      // A trial-limit denial (see the "Gate AI workflows..." ticket) is a
      // deliberate, permanent block for this team/bucket, not a real
      // evaluation failure -- show the actual reason instead of the generic
      // "no results found" fallback below, which would otherwise mislead the
      // user into thinking their search/entity was the problem.
      const isTrialLimitDenial = error?.response?.status === 402;
      const data = {
        hs_code: isTrialLimitDenial ? 'Trial run used' : 'Error evaluating threat level',
        risk_level: '',
        burden: '',
        key_factors: [],
        trade_category: '',
        explanation: isTrialLimitDenial
          ? error.response.data?.detail ||
            `Your team's one trial run of the ${
              type === 'Import' ? 'Import Workflow' : 'Export Workflow'
            } has been used. Upgrade your plan to keep using it.`
          : 'There were no results found for this search to be evaluated please try again with a different Entity',
        screenDate: new Date().toLocaleDateString(),
      };
      return data;
    });
};

export const runAIEvaluationSingleProduct = async (
  code: string,
  origin: string,
  destination: string,
  hits: string,
  type: string,
  groupId?: string,
  source?: 'workflow' | 'screening',
  // The Import Workflow run this screening belongs to: its HTS classification reserved the trial
  // for that run, and this step carries it on.
  runId?: string,
): Promise<ThreatEvaluationResponseProduct | null> => {
  const request: ThreatEvaluationRequestProduct = {
    hs_code: code,
    origin_country: origin,
    destination_country: destination,
    possible_matches_json: hits,
    group_id: groupId,
    source: source,
    ...(runId ? { run_id: runId } : {}),
  };
  return runAIEvaluationProducts(request, type);
};
